import React from 'react';
import Box from '@mui/material/Box';
import find from 'lodash-es/find';
import Slider from 'react-slick';
import { CustomArrowProps } from 'react-slick';
import LazyLoader from '../LazyLoader';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import './react-slick.css';

// MUI 5
import { ProductCard } from '../ProductCard';

interface ProductCardListType {
  products: any;
  handles?: string[];
  tag?: string;
  className?: string;
}

const SlickArrowLeft = ({ currentSlide, slideCount, ...props }: CustomArrowProps) => {
  return (
    <NavigateBeforeIcon
      fontSize="large"
      sx={{
        borderRadius: '50%',
        width: { xs: '1.5rem', lg: '2rem' },
        height: { xs: '1.5rem', lg: '2rem' },
        zIndex: '100',
      }}
      {...props}
      className={'slick-prev slick-arrow' + (currentSlide === 0 ? ' slick-disabled' : '')}
      aria-hidden="true"
      aria-disabled={currentSlide === 0 ? true : false}
    />
  );
};

const SlickArrowRight = ({ currentSlide, slideCount, ...props }: CustomArrowProps) => {
  return (
    <NavigateNextIcon
      fontSize="large"
      sx={{
        borderRadius: '50%',
        width: { xs: '1.5rem', lg: '2rem' },
        height: { xs: '1.5rem', lg: '2rem' },
        zIndex: '100',
      }}
      {...props}
      className={'slick-next slick-arrow' + (currentSlide === slideCount - 1 ? ' slick-disabled' : '')}
      aria-hidden="true"
      aria-disabled={currentSlide === slideCount - 1 ? true : false}
    />
  );
};

const SlickProps = {
  dots: false,
  infinite: true,
  lazyLoad: true,
  adaptiveHeight: true,
  speed: 1000,
  slidesToShow: 4,
  slidesToScroll: 3,
  initialSlide: 0,
  slide: 'a',
  prevArrow: <SlickArrowLeft />,
  nextArrow: <SlickArrowRight />,
  responsive: [
    {
      breakpoint: 1280,
      settings: {
        dots: true,
        slidesToShow: 4,
        slidesToScroll: 3,
        initialSlide: 0,
      },
    },
    {
      breakpoint: 768,
      settings: {
        dots: true,
        slidesToShow: 3,
        slidesToScroll: 2,
        initialSlide: 0,
      },
    },
    {
      breakpoint: 450,
      settings: {
        dots: false,
        slidesToShow: 2,
        slidesToScroll: 1,
        initialSlide: 0,
      },
    },
  ],
};

export const SlickProductCardList: React.FC<ProductCardListType> = ({ products, handles, tag, className }) => {
  return (
    <Box
      sx={{
        minHeight: {
          xs: 'calc(100vw / 2 - 10px + 80px)',
          sm: 'calc(100vw / 3 - 10px + 90px)',
          md: 'calc(100vw / 4 - 10px + 10px)',
          lg: '410px',
        },
      }}
      className="cardlist">
      <Slider
        {...SlickProps}
        lazyLoad="progressive"
        customPaging={(i) => (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              bottom: '-20px',
              opacity: 0,
            }}>
            {i + 1}
          </div>
        )}>
        {handles
          ? handles.map((handle) => {
              const product = find(products, ({ node }) => handle === node.handle);
              return product ? (
                <Box key={`LazySlick-${handle}`} sx={{ width: { xs: '50%', md: '33%', pad: '25%' }, minHeight: '260px' }}>
                  <LazyLoader
                    priority={LazyLoader.Priority.LOW}
                    predictSize={{ width: 320, height: 410 }}
                    placeholder={
                      <Box
                        sx={{
                          width: {
                            xs: 'cal(100% - 5px)',
                            lg: '320px',
                          },
                          height: { xs: '265px', sm: '305px', md: '340px', pad: '355px', lg: '410px' },
                          backgroundColor: '#FFFFFF',
                          margin: '1px 1px', // 保持轮播项间距
                        }}
                      />
                    }>
                    <ProductCard key={`LazySlick-${handle}`} product={product.node} />
                  </LazyLoader>
                </Box>
              ) : null;
            })
          : tag}
      </Slider>
    </Box>
  );
};

export const ProductCardList: React.FC<ProductCardListType> = ({ products, handles, tag, className }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
      {handles?.map((handle) => {
        const product = find(products, ({ node }) => handle === node.handle);
        return product ? (
          <Box key={`LazyCard-${handle}`} sx={{ width: { xs: '50%', sm: '33%', md: '25%' }, minHeight: '280px' }}>
            <LazyLoader
              priority={LazyLoader.Priority.LOW}
              predictSize={{ width: 313, height: 400 }}
              placeholder={
                <Box
                  sx={{
                    width: {
                      xs: 'cal(100% - 5px)',
                      lg: '313px',
                    },
                    height: { xs: '283px', sm: '298px', md: '329px', pad: '345px', lg: '400px' },
                    backgroundColor: '#FFFFFF',
                    margin: '1px 1px', // 保持轮播项间距
                  }}
                />
              }>
              <ProductCard product={product.node} />
            </LazyLoader>
          </Box>
        ) : null;
      })}
    </Box>
  );
};
